import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ArrowLeft, Clock, CheckCircle2, XCircle, AlertCircle, Copy, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/lib/utils';
import { useAuth } from '@/shared/hooks/useAuth';

interface TransactionRequest {
    id: string;
    userId: string;
    userName: string;
    type: 'deposit' | 'withdrawal';
    currency: string; // 'KRW' | 'BTC' | 'ETH' | etc
    amount: number;
    status: 'pending' | 'account_issued' | 'deposit_claimed' | 'approved' | 'rejected';

    // KRW deposit specific
    virtualAccount?: {
        bank: string;
        accountNumber: string;
        accountHolder: string;
    };

    // KRW withdrawal specific
    withdrawalBankInfo?: {
        bank: string;
        accountNumber: string;
        accountHolder: string;
    };

    // Crypto deposit specific
    depositAddress?: string;

    // Crypto withdrawal specific
    withdrawalAddress?: string;

    rejectionReason?: string;
    createdAt: any;
    updatedAt: any;
    processedAt?: any;
}

const MyTransactionRequestsPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [requests, setRequests] = useState<TransactionRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'deposit' | 'withdrawal'>('all');

    useEffect(() => {
        // Set timeout to prevent infinite loading
        const loadingTimeout = setTimeout(() => {
            setLoading(false);
        }, 10000); // 10 second timeout

        try {
            if (!user) {
                setLoading(false);
                return;
            }

            const q = query(
                collection(db, 'transactionRequests'),
                where('userId', '==', user.uid)
            );

            const unsubscribe = onSnapshot(
                q,
                (snapshot) => {
                    const data = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    })) as TransactionRequest[];

                    // Sort by latest
                    data.sort((a, b) => {
                        const aTime = a.createdAt?.toMillis?.() || 0;
                        const bTime = b.createdAt?.toMillis?.() || 0;
                        return bTime - aTime;
                    });

                    setRequests(data);
                    setLoading(false);
                    clearTimeout(loadingTimeout);
                },
                (error) => {
                    console.error('Error fetching transaction requests:', error);
                    setLoading(false);
                    clearTimeout(loadingTimeout);
                    alert('데이터를 불러오는 중 오류가 발생했습니다.');
                }
            );

            return () => {
                unsubscribe();
                clearTimeout(loadingTimeout);
            };
        } catch (error) {
            console.error('Error setting up listener:', error);
            setLoading(false);
            clearTimeout(loadingTimeout);
        }
    }, [user]);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        alert('복사되었습니다.');
    };

    const handleDepositClaimed = async (requestId: string) => {
        if (!confirm('입금을 완료하셨나요?')) return;

        try {
            await updateDoc(doc(db, 'transactionRequests', requestId), {
                status: 'deposit_claimed',
                claimedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            alert('입금 완료 신청이 접수되었습니다.\n관리자 확인 후 승인됩니다.');
        } catch (error) {
            console.error('입금 완료 신청 실패:', error);
            alert('입금 완료 신청 중 오류가 발생했습니다.');
        }
    };

    const getStatusInfo = (status: string) => {
        switch (status) {
            case 'pending':
                return { label: '계좌 발급 대기', color: 'text-amber-600 dark:text-amber-500', bg: 'bg-amber-500/10', icon: Clock };
            case 'account_issued':
                return { label: '계좌 발급됨', color: 'text-blue-600 dark:text-blue-500', bg: 'bg-blue-500/10', icon: CheckCircle2 };
            case 'deposit_claimed':
                return { label: '관리자 확인 중', color: 'text-violet-600 dark:text-violet-500', bg: 'bg-violet-500/10', icon: Clock };
            case 'approved':
                return { label: '승인 완료', color: 'text-emerald-600 dark:text-emerald-500', bg: 'bg-emerald-500/10', icon: CheckCircle2 };
            case 'rejected':
                return { label: '거절됨', color: 'text-rose-600 dark:text-rose-500', bg: 'bg-rose-500/10', icon: XCircle };
            default:
                return { label: status, color: 'text-gray-600', bg: 'bg-gray-500/10', icon: Clock };
        }
    };

    const filteredRequests = filter === 'all'
        ? requests
        : requests.filter(r => r.type === filter);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-24 text-foreground animate-in fade-in duration-1000">
            <div className="max-w-4xl mx-auto px-4 lg:px-8 space-y-8 pt-8">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/wallet')}
                        className="p-3 rounded-xl bg-card border border-border hover:bg-accent transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                        <h1 className="text-3xl font-black tracking-tighter uppercase">
                            입출금 신청 내역
                        </h1>
                        <p className="text-sm text-muted-foreground font-bold mt-1">
                            My Transaction Requests [v3.1.3]
                        </p>
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex gap-2 overflow-x-auto pb-2">
                    {[
                        { value: 'all', label: '전체' },
                        { value: 'deposit', label: '입금' },
                        { value: 'withdrawal', label: '출금' }
                    ].map((tab) => (
                        <button
                            key={tab.value}
                            onClick={() => setFilter(tab.value as any)}
                            className={cn(
                                "px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest whitespace-nowrap transition-all",
                                filter === tab.value
                                    ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                                    : "bg-card border border-border text-muted-foreground hover:bg-accent"
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Requests List */}
                <div className="space-y-4">
                    {filteredRequests.length === 0 ? (
                        <div className="text-center py-16 px-4 bg-card border border-border rounded-[2rem]">
                            <AlertCircle className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                            <p className="text-muted-foreground font-bold">신청 내역이 없습니다.</p>
                        </div>
                    ) : (
                        filteredRequests.map((request) => {
                            const statusInfo = getStatusInfo(request.status);
                            const StatusIcon = statusInfo.icon;
                            const TypeIcon = request.type === 'deposit' ? ArrowDownLeft : ArrowUpRight;
                            const typeColor = request.type === 'deposit' ? 'text-blue-600' : 'text-rose-600';
                            const typeBg = request.type === 'deposit' ? 'bg-blue-500/10' : 'bg-rose-500/10';

                            return (
                                <div
                                    key={request.id}
                                    className="bg-card border border-border rounded-[2rem] p-8 space-y-6"
                                >
                                    {/* Header */}
                                    <div className="flex items-center justify-between flex-wrap gap-4">
                                        <div className="flex items-center gap-3">
                                            <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", typeBg)}>
                                                <TypeIcon className={cn("h-6 w-6", typeColor)} />
                                            </div>
                                            <div>
                                                <p className="text-lg font-black">
                                                    {request.type === 'deposit' ? '입금' : '출금'} • {request.currency}
                                                </p>
                                                <p className="text-xs text-muted-foreground font-bold">
                                                    {request.createdAt?.toDate?.().toLocaleString('ko-KR')}
                                                </p>
                                            </div>
                                        </div>
                                        <div className={cn("flex items-center gap-2 px-4 py-2 rounded-full font-black text-xs uppercase tracking-widest", statusInfo.bg, statusInfo.color)}>
                                            <StatusIcon className="h-4 w-4" />
                                            {statusInfo.label}
                                        </div>
                                    </div>

                                    {/* Amount */}
                                    <div className="space-y-1">
                                        <p className="text-xs text-muted-foreground font-black uppercase tracking-widest">금액</p>
                                        <p className="text-3xl font-black tracking-tighter tabular-nums">
                                            {request.currency === 'KRW'
                                                ? `₩${request.amount.toLocaleString()}`
                                                : `${request.amount} ${request.currency}`
                                            }
                                        </p>
                                    </div>

                                    {/* KRW Deposit - Pending Message */}
                                    {request.type === 'deposit' && request.currency === 'KRW' && request.status === 'pending' && (
                                        <div className="p-6 rounded-xl bg-blue-500/10 border border-blue-500/20">
                                            <p className="text-sm font-bold text-blue-600 dark:text-blue-500">
                                                ⏳ 관리자가 가상계좌를 발급 중입니다. 잠시만 기다려주세요.
                                            </p>
                                        </div>
                                    )}

                                    {/* KRW Deposit - Virtual Account */}
                                    {request.type === 'deposit' && request.currency === 'KRW' && request.virtualAccount && (
                                        <div className="space-y-4 p-6 rounded-xl bg-accent/30 border border-border">
                                            <p className="text-xs font-black text-muted-foreground uppercase tracking-widest">
                                                발급된 가상계좌
                                            </p>
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-bold text-muted-foreground">은행</span>
                                                    <span className="font-black">{request.virtualAccount.bank}</span>
                                                </div>
                                                <div className="flex items-center justify-between gap-4">
                                                    <span className="text-sm font-bold text-muted-foreground">계좌번호</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-mono font-black">{request.virtualAccount.accountNumber}</span>
                                                        <button
                                                            onClick={() => copyToClipboard(request.virtualAccount!.accountNumber)}
                                                            className="p-1 rounded hover:bg-accent transition-colors"
                                                        >
                                                            <Copy className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-bold text-muted-foreground">예금주</span>
                                                    <span className="font-black">{request.virtualAccount.accountHolder}</span>
                                                </div>
                                            </div>

                                            {/* 입금 완료 버튼 - account_issued 상태일 때만 표시 */}
                                            {request.status === 'account_issued' && (
                                                <button
                                                    onClick={() => handleDepositClaimed(request.id)}
                                                    className="w-full py-3 rounded-xl bg-blue-600 text-white font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-blue-600/20"
                                                >
                                                    입금 완료
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* KRW Withdrawal - Bank Info */}
                                    {request.type === 'withdrawal' && request.currency === 'KRW' && request.withdrawalBankInfo && (
                                        <div className="space-y-4 p-6 rounded-xl bg-accent/30 border border-border">
                                            <p className="text-xs font-black text-muted-foreground uppercase tracking-widest">
                                                출금 계좌 정보
                                            </p>
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-bold text-muted-foreground">은행</span>
                                                    <span className="font-black">{request.withdrawalBankInfo.bank}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-bold text-muted-foreground">계좌번호</span>
                                                    <span className="font-mono font-black">{request.withdrawalBankInfo.accountNumber}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-bold text-muted-foreground">예금주</span>
                                                    <span className="font-black">{request.withdrawalBankInfo.accountHolder}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Crypto Deposit - Address */}
                                    {request.type === 'deposit' && request.currency !== 'KRW' && request.depositAddress && (
                                        <div className="space-y-2 p-6 rounded-xl bg-accent/30 border border-border">
                                            <p className="text-xs font-black text-muted-foreground uppercase tracking-widest">
                                                입금 주소
                                            </p>
                                            <div className="flex items-center gap-2">
                                                <p className="font-mono text-sm font-bold flex-1 break-all">{request.depositAddress}</p>
                                                <button
                                                    onClick={() => copyToClipboard(request.depositAddress!)}
                                                    className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors flex-shrink-0"
                                                >
                                                    <Copy className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Crypto Withdrawal - Address */}
                                    {request.type === 'withdrawal' && request.currency !== 'KRW' && request.withdrawalAddress && (
                                        <div className="space-y-2 p-6 rounded-xl bg-accent/30 border border-border">
                                            <p className="text-xs font-black text-muted-foreground uppercase tracking-widest">
                                                출금 주소
                                            </p>
                                            <p className="font-mono text-sm font-bold break-all">{request.withdrawalAddress}</p>
                                        </div>
                                    )}

                                    {/* Rejection Reason */}
                                    {request.status === 'rejected' && request.rejectionReason && (
                                        <div className="p-6 rounded-xl bg-rose-500/10 border border-rose-500/20">
                                            <p className="text-xs font-black text-rose-600 dark:text-rose-500 uppercase tracking-widest mb-2">
                                                거절 사유
                                            </p>
                                            <div className="text-sm font-bold text-foreground whitespace-pre-wrap">
                                                {request.rejectionReason?.replace(/\\n/g, '\n')}
                                            </div>
                                        </div>
                                    )}

                                    {/* Approved Message */}
                                    {request.status === 'approved' && (
                                        <div className="p-6 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                                            <p className="text-sm font-bold text-emerald-600 dark:text-emerald-500">
                                                ✓ {request.type === 'deposit' ? '입금이 승인되어 잔액에 반영되었습니다.' : '출금이 승인되어 처리되었습니다.'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyTransactionRequestsPage;
